// src/lib/cabinetService.js
// Cabinet de curiosités (lot 2) : accès à la base. Les écritures passent uniquement par les fonctions
// cabinet_create / cabinet_save_revision ; la lecture est limitée au propriétaire par les règles d'accès.
import { supabase } from "./supabaseClient";
import { cabinetText } from "./cabinetI18n";

export const CABINET_THEMES = [
  { id: "ardoise", label: "Ardoise", key: "theme_ardoise", wall: "#2b2f36", frame: "#c8a96a", ink: "#f2efe8" },
  { id: "bordeaux", label: "Bordeaux", key: "theme_bordeaux", wall: "#4a1f2a", frame: "#d4b46a", ink: "#f6ece6" },
  { id: "vert", label: "Vert anglais", key: "theme_vert", wall: "#23382f", frame: "#c9b277", ink: "#eef0e6" },
  { id: "lin", label: "Lin", key: "theme_lin", wall: "#e9e3d6", frame: "#6b5a3c", ink: "#2a2418" },
];
export const MAX_ITEMS = 100;
export const MAX_CAPTION = 280;

export const themeById = (id) => CABINET_THEMES.find((t) => t.id === id) || CABINET_THEMES[0];

export async function isCabinetEnabled() {
  try {
    const { data, error } = await supabase.rpc("cabinet_enabled");
    if (error) return false;
    return data === true;
  } catch {
    return false;
  }
}

// Messages lisibles pour les erreurs levées par les fonctions SQL.
export function cabinetErrorMessage(err, lang = "fr") {
  const m = String(err?.message || err || "");
  if (m.includes("VERSION_CONFLICT")) return cabinetText(lang, "e_conflict");
  if (m.includes("ARTWORK_NOT_ALLOWED")) return cabinetText(lang, "e_artwork");
  if (m.includes("FEATURE_DISABLED")) return cabinetText(lang, "e_disabled");
  if (m.includes("COLLECTION_NOT_OWNED")) return cabinetText(lang, "e_notOwned");
  if (m.includes("TITLE_REQUIRED")) return cabinetText(lang, "e_titleRequired");
  return cabinetText(lang, "e_generic");
}

export async function listCabinets(collectionId) {
  const { data, error } = await supabase
    .from("curiosity_cabinets")
    .select("id,title,status,current_revision_id,updated_at")
    .eq("collection_id", collectionId)
    .eq("status", "active")
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createCabinet(collectionId, title) {
  const { data, error } = await supabase.rpc("cabinet_create", { p_collection_id: collectionId, p_title: title });
  if (error) throw error;
  return data; // { cabinet_id, revision_id, version }
}

export async function listRevisions(cabinetId) {
  const { data, error } = await supabase
    .from("curiosity_revisions")
    .select("id,version,origin,created_at")
    .eq("cabinet_id", cabinetId)
    .order("version", { ascending: false })
    .limit(30);
  if (error) throw error;
  return data || [];
}

// Charge une révision (la dernière par défaut) avec ses œuvres, dans l'ordre d'accrochage.
export async function loadRevision(cabinetId, version = null) {
  let q = supabase
    .from("curiosity_revisions")
    .select("id,version,config")
    .eq("cabinet_id", cabinetId);
  q = version ? q.eq("version", version) : q.order("version", { ascending: false }).limit(1);
  const { data: rev, error } = await q.maybeSingle();
  if (error) throw error;
  if (!rev) return null;
  const { data: items, error: e2 } = await supabase
    .from("curiosity_revision_items")
    .select("position,artwork_id,media_ref,display")
    .eq("revision_id", rev.id)
    .order("position", { ascending: true });
  if (e2) throw e2;
  return { ...rev, items: items || [] };
}

export async function saveRevision(cabinetId, expectedVersion, config, items) {
  const payload = items
    .filter((it) => it.artwork_id)
    .map((it) => ({ artwork_id: it.artwork_id, media_ref: it.media_ref || null, display: it.display || {} }));
  const { data, error } = await supabase.rpc("cabinet_save_revision", {
    p_cabinet_id: cabinetId,
    p_expected_version: expectedVersion,
    p_config: config,
    p_items: payload,
    p_origin: "manual",
  });
  if (error) throw error;
  return data; // { cabinet_id, revision_id, version }
}

export async function renameCabinet(cabinetId, title) {
  const { error } = await supabase.from("curiosity_cabinets").update({ title }).eq("id", cabinetId);
  if (error) throw error;
}

export async function archiveCabinet(cabinetId) {
  const { error } = await supabase.from("curiosity_cabinets").update({ status: "archived" }).eq("id", cabinetId);
  if (error) throw error;
}

// ── Images : on ne stocke qu'un identifiant d'image (media_ref) ; l'adresse est reconstruite à l'affichage.
export const parseImages = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
};

// Identifiant stable d'une image : son id, sinon son chemin, sinon son adresse sans signature.
export const mediaRefOf = (img) => {
  if (!img) return null;
  if (typeof img === "string") return img.split("?")[0].slice(0, 120);
  return String(img.id || img.path || (img.url || "").split("?")[0] || "").slice(0, 120) || null;
};

export function pickImage(images, mediaRef = null) {
  const arr = parseImages(images);
  if (!arr.length) return null;
  if (mediaRef) {
    const hit = arr.find((x) => mediaRefOf(x) === mediaRef);
    if (hit) return hit;
  }
  return arr.find((x) => x?.isPrimary || x?.is_primary) || arr[0];
}

const signedCache = new Map();

// Retourne une adresse affichable. Si l'image a un chemin de stockage privé, on génère une adresse signée neuve.
export async function resolveImageUrl(img) {
  if (!img) return null;
  if (typeof img === "string") return img;
  if (img.bucket && img.path) {
    const key = `${img.bucket}/${img.path}`;
    const hit = signedCache.get(key);
    if (hit && hit.exp > Date.now()) return hit.url;
    const { data } = await supabase.storage.from(img.bucket).createSignedUrl(img.path, 3600);
    if (data?.signedUrl) {
      signedCache.set(key, { url: data.signedUrl, exp: Date.now() + 50 * 60 * 1000 });
      return data.signedUrl;
    }
  }
  return img.url || img.publicUrl || img.public_url || img.path || null;
}

// ── Partage par invitation (destinataire identifié par son e-mail) ──────────────────────────
export function shareErrorMessage(err, lang = "fr") {
  const m = String(err?.message || err || "");
  if (m.includes("SELF_SHARE")) return cabinetText(lang, "e_self");
  if (m.includes("EMAIL_INVALID")) return cabinetText(lang, "e_email");
  if (m.includes("ALREADY_SHARED")) return cabinetText(lang, "e_already");
  if (m.includes("TOO_MANY_SHARES")) return cabinetText(lang, "e_tooMany");
  if (m.includes("DURATION_INVALID")) return cabinetText(lang, "e_duration");
  return cabinetErrorMessage(err, lang);
}

export async function shareRevision(cabinetId, revisionId, email, days = 90) {
  const { data, error } = await supabase.rpc("cabinet_share_create", {
    p_cabinet_id: cabinetId, p_revision_id: revisionId, p_email: email, p_days: days,
  });
  if (error) throw error;
  return data;
}

export async function revokeShare(shareId) {
  const { error } = await supabase.rpc("cabinet_share_revoke", { p_share_id: shareId });
  if (error) throw error;
}

export async function listShares(cabinetId) {
  const { data, error } = await supabase
    .from("curiosity_shares")
    .select("id,revision_id,recipient_email,expires_at,revoked_at,created_at")
    .eq("cabinet_id", cabinetId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  const ids = (data || []).map((s) => s.id);
  let counts = {};
  if (ids.length) {
    const { data: acc } = await supabase.from("curiosity_share_access").select("share_id").in("share_id", ids);
    (acc || []).forEach((a) => { counts[a.share_id] = (counts[a.share_id] || 0) + 1; });
  }
  return (data || []).map((s) => ({ ...s, views: counts[s.id] || 0 }));
}

export async function receivedShares() {
  const { data, error } = await supabase.rpc("cabinet_shares_received");
  if (error) throw error;
  return data || [];
}

export async function fetchSharedCabinet(shareId) {
  const { data, error } = await supabase.functions.invoke("cabinet-share-view", { body: { share_id: shareId } });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}
